/**
 * Same-origin GET single-fetch requests (`*.data`, `/__manifest`) are retried once, and a `*.data` request that still
 * fails triggers at most one document navigation per URL per guard window.
 */
export type FetchLike = (_input: RequestInfo | URL, _init?: RequestInit) => Promise<Response>;

export type RecoveryKind = 'retry' | 'recovered' | 'fallback' | 'gave-up';
export type RecoveryReason = 'network-error' | 'body-read-error' | 'server-error' | 'bad-content-type' | 'ok';

export interface RecoveryReport {
  kind: RecoveryKind;
  reason: RecoveryReason;
  /** Pathname only - never the query string. */
  url: string;
  attempt: number;
  elapsedMs: number;
  error?: string;
  status?: number;
  contentType?: string | null;
  contentLength?: number | null;
}

type RecoveryStorage = Pick<Storage, 'getItem' | 'setItem'>;

export interface ResilientFetchOptions {
  fetch: FetchLike;
  isSameOrigin?: (_url: URL) => boolean;
  retryDelayMs?: number;
  fallbackHoldMs?: number;
  guardMs?: number;
  sleep?: (_ms: number) => Promise<void>;
  now?: () => number;
  report?: (_report: RecoveryReport) => void;
  fallback?: (_documentUrl: string) => void;
  storage?: RecoveryStorage | null;
  /** Number of `pagehide` events seen so far; a change during a request means the page is going away. */
  pageHides?: () => number;
}

export const RECOVERY_GUARD_MS = 60_000;

const MAX_ATTEMPTS = 2;
const TURBO_STREAM_CONTENT_TYPE = 'text/x-script';
const NO_BODY_STATUSES = new Set([204, 205, 304]);
const STORAGE_PREFIX = 'sf:recover:';

let pageHideCount = 0;
let fallbackIssued = false;

const TRANSIENT_TYPE_ERROR =
  /network error|load failed|failed to fetch|networkerror when attempting|network request failed|network connection was lost|internet connection appears to be offline/i;
const TRANSIENT_ERROR = /unable to decode turbo-stream|manifest version mismatch|single-fetch: bad response/i;

export function isSingleFetchUrl(url: URL): boolean {
  return url.pathname.endsWith('.data') || url.pathname === '/__manifest';
}

export function documentUrlFor(dataUrl: URL): string {
  const url = new URL(dataUrl.href);
  const pathname = url.pathname === '/_root.data' ? '/' : url.pathname.replace(/\.data$/, '');
  url.pathname = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
  url.searchParams.delete('_routes');
  return url.pathname + url.search;
}

export function isTransientNavigationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (typeof message !== 'string') return false;
  if (name === 'TypeError' || error instanceof TypeError) return TRANSIENT_TYPE_ERROR.test(message);
  return TRANSIENT_ERROR.test(message);
}

/** True (and the slot is taken) unless a recovery for `key` was already claimed within `guardMs`. */
export function claimRecoverySlot(
  storage: RecoveryStorage | null | undefined,
  key: string,
  now: number,
  guardMs: number
): boolean {
  if (!storage) return true;
  const storageKey = STORAGE_PREFIX + key;
  try {
    const last = Number(storage.getItem(storageKey));
    if (Number.isFinite(last) && last > 0 && now - last < guardMs) return false;
    storage.setItem(storageKey, String(now));
    return true;
  } catch {
    return false;
  }
}

function unavailable(): never {
  throw new Error('single-fetch: sessionStorage is unavailable');
}

/** Unreadable storage must block the document-reload fallback, not disable its loop guard. */
const UNAVAILABLE_STORAGE: RecoveryStorage = { getItem: unavailable, setItem: unavailable };

function defaultStorage(): RecoveryStorage {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage ? sessionStorage : UNAVAILABLE_STORAGE;
  } catch {
    return UNAVAILABLE_STORAGE;
  }
}

function toUrl(input: RequestInfo | URL): URL | null {
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    return new URL(raw, typeof location !== 'undefined' ? location.href : undefined);
  } catch {
    return null;
  }
}

function methodOf(input: RequestInfo | URL, init?: RequestInit): string {
  const method = init?.method ?? (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET');
  return method.toUpperCase();
}

function isAbort(error: unknown, init?: RequestInit): boolean {
  if (init?.signal?.aborted) return true;
  return !!error && typeof error === 'object' && (error as { name?: unknown }).name === 'AbortError';
}

function describe(error: unknown): string {
  if (error && typeof error === 'object') {
    const { name, message } = error as { name?: unknown; message?: unknown };
    return `${String(name ?? 'Error')}: ${String(message ?? '')}`;
  }
  return String(error);
}

type Verdict =
  | { action: 'passthrough' }
  | { action: 'buffer' }
  | { action: 'retry'; reason: 'server-error' | 'bad-content-type' };

function classify(res: Response): Verdict {
  if (NO_BODY_STATUSES.has(res.status)) return { action: 'passthrough' };
  const contentType = res.headers.get('content-type') ?? '';
  if (res.headers.has('x-remix-response') || contentType.includes(TURBO_STREAM_CONTENT_TYPE))
    return { action: 'buffer' };
  if (res.status >= 500) return { action: 'retry', reason: 'server-error' };
  return { action: 'retry', reason: 'bad-content-type' };
}

function isManifestPatchSet(value: unknown): value is Record<string, { id: string }> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every(
      (route) =>
        typeof route === 'object' &&
        route !== null &&
        !Array.isArray(route) &&
        typeof (route as { id?: unknown }).id === 'string'
    )
  );
}

function responseWithBody(res: Response, body: ArrayBuffer): Response {
  const headers = new Headers(res.headers);
  headers.delete('content-encoding');
  headers.delete('content-length');
  return new Response(body, { status: res.status, statusText: res.statusText, headers });
}

/**
 * Reads the whole body so a stream cut mid-transfer fails here, not inside Remix's decoder; it also waits for every
 * deferred (streamed) value, so `defer`/`Await` would lose streaming through this wrapper.
 */
async function buffered(res: Response): Promise<Response> {
  const body = await res.arrayBuffer();
  const headers = new Headers(res.headers);
  headers.delete('content-encoding');
  headers.delete('content-length');
  return new Response(body, { status: res.status, statusText: res.statusText, headers });
}

/**
 * A page that fired pagehide is gone or returns from bfcache, where a rejection would complete its superseded
 * navigation with an error.
 */
function superseded(): Promise<Response> {
  return new Promise<Response>(() => undefined);
}

export function createResilientFetch(options: ResilientFetchOptions): FetchLike {
  const { fetch: baseFetch, retryDelayMs = 500, fallbackHoldMs = 8000, guardMs = RECOVERY_GUARD_MS } = options;
  const isSameOrigin =
    options.isSameOrigin ?? ((url: URL) => typeof location !== 'undefined' && url.origin === location.origin);
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const now = options.now ?? (() => Date.now());
  const pageHides = options.pageHides ?? (() => pageHideCount);
  const onReport = options.report;
  const report = (entry: RecoveryReport) => {
    try {
      onReport?.(entry);
    } catch {
      // A throwing observer must never change the outcome of a request.
    }
  };
  const fallback =
    options.fallback ??
    ((documentUrl: string) => {
      window.location.assign(window.location.origin + documentUrl);
    });
  const storage = options.storage === undefined ? defaultStorage() : options.storage;

  return async function resilientFetch(input, init) {
    const url = toUrl(input);
    if (!url || methodOf(input, init) !== 'GET' || !isSameOrigin(url) || !isSingleFetchUrl(url)) {
      return baseFetch(input, init);
    }

    const isManifest = url.pathname === '/__manifest';
    const started = now();
    const hidesAtStart = pageHides();
    const unloading = () => pageHides() !== hidesAtStart;
    const make = (attempt: number, extra: Partial<RecoveryReport>): RecoveryReport => ({
      kind: 'retry',
      reason: 'network-error',
      url: url.pathname,
      attempt,
      elapsedMs: now() - started,
      ...extra,
    });
    let lastError: unknown = new Error('single-fetch: no attempts made');
    let lastReason: RecoveryReason = 'network-error';
    let lastMeta: Partial<RecoveryReport> = {};
    /**
     * Resolves false, with nothing reported, once the page is unloading; otherwise reports a failed attempt that will
     * be retried (the last attempt and every manifest attempt are reported by their outcome only) and waits for it.
     */
    const afterFailure = async (attempt: number, entry: Partial<RecoveryReport>): Promise<boolean> => {
      if (unloading()) return false;
      if (attempt >= MAX_ATTEMPTS) return true;
      if (!isManifest) report(make(attempt, entry));
      await sleep(retryDelayMs);
      return !unloading();
    };

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      let res: Response;
      try {
        res = await baseFetch(input, attempt === 1 ? init : { ...init, cache: 'reload' });
      } catch (error) {
        if (isAbort(error, init)) throw error;
        lastError = error;
        lastReason = 'network-error';
        lastMeta = {};
        if (!(await afterFailure(attempt, { reason: lastReason, error: describe(error) }))) return superseded();
        continue;
      }

      if (isManifest) {
        // Remix uses 204 + X-Remix-Reload-Document to request a document reload.
        // Preserve that response unchanged so the router can handle the protocol.
        if (res.status === 204 && res.headers.has('x-remix-reload-document')) return res;

        const meta = {
          status: res.status,
          contentType: res.headers.get('content-type'),
          contentLength: res.headers.has('content-length') ? Number(res.headers.get('content-length')) : null,
        };

        if (res.status >= 400 && res.status < 500) return res;

        if (res.status >= 500) {
          void res.body?.cancel().catch(() => undefined);
          lastError = new Error(`single-fetch: bad response ${res.status} ${meta.contentType ?? ''}`.trim());
          lastReason = 'server-error';
          lastMeta = meta;
          if (!(await afterFailure(attempt, { reason: lastReason, ...meta }))) return superseded();
          continue;
        }
        if (!res.ok) return res;

        const contentType = meta.contentType?.split(';', 1)[0].trim().toLowerCase() ?? '';
        if (contentType !== 'application/json' && !contentType.endsWith('+json')) {
          void res.body?.cancel().catch(() => undefined);
          lastError = new TypeError(`single-fetch: invalid manifest content type ${contentType || '(missing)'}`);
          lastReason = 'bad-content-type';
          lastMeta = meta;
          if (!(await afterFailure(attempt, { reason: lastReason, error: describe(lastError), ...meta })))
            return superseded();
          continue;
        }

        try {
          // Read the network body once; parsing a clone would tee and retain the unread branch.
          const body = await res.arrayBuffer();
          const parsed: unknown = JSON.parse(new TextDecoder().decode(body));
          if (!isManifestPatchSet(parsed)) throw new TypeError('single-fetch: invalid manifest patch object');
          if (attempt > 1) report(make(attempt, { kind: 'recovered', reason: 'ok', ...meta }));
          return responseWithBody(res, body);
        } catch (error) {
          if (isAbort(error, init)) throw error;
          lastError = error;
          lastReason = 'body-read-error';
          lastMeta = meta;
          if (!(await afterFailure(attempt, { reason: lastReason, error: describe(error), ...meta })))
            return superseded();
        }
        continue;
      }

      const verdict = classify(res);
      if (verdict.action === 'passthrough') return res;

      const meta = {
        status: res.status,
        contentType: res.headers.get('content-type'),
        contentLength: res.headers.has('content-length') ? Number(res.headers.get('content-length')) : null,
      };

      if (verdict.action === 'retry') {
        void res.body?.cancel().catch(() => undefined);
        lastError = new Error(`single-fetch: bad response ${res.status} ${meta.contentType ?? ''}`.trim());
        lastReason = verdict.reason;
        lastMeta = meta;
        if (!(await afterFailure(attempt, { reason: lastReason, ...meta }))) return superseded();
        continue;
      }

      try {
        const complete = await buffered(res);
        if (attempt > 1) report(make(attempt, { kind: 'recovered', reason: 'ok', ...meta }));
        return complete;
      } catch (error) {
        if (isAbort(error, init)) throw error;
        lastError = error;
        lastReason = 'body-read-error';
        lastMeta = meta;
        if (!(await afterFailure(attempt, { reason: lastReason, error: describe(error), ...meta })))
          return superseded();
      }
    }

    // Assumes every .data request is part of a navigation, so a GET fetcher would hard-navigate to its own URL.
    if (!isManifest) {
      const documentUrl = documentUrlFor(url);
      if (claimRecoverySlot(storage, documentUrl, now(), guardMs)) {
        report(make(MAX_ATTEMPTS, { kind: 'fallback', reason: lastReason, error: describe(lastError), ...lastMeta }));
        const hidesAtFallback = pageHides();
        fallbackIssued = true;
        fallback(documentUrl);
        // Give the document navigation time to unload the page before Remix sees a rejection.
        await sleep(fallbackHoldMs);
        if (pageHides() !== hidesAtFallback) return superseded();
        throw lastError;
      }
    }

    report(make(MAX_ATTEMPTS, { kind: 'gave-up', reason: lastReason, error: describe(lastError), ...lastMeta }));
    throw lastError;
  };
}

let installed = false;

export function installSingleFetchResilience(options: Omit<ResilientFetchOptions, 'fetch'> = {}): void {
  if (installed || typeof window === 'undefined') return;
  try {
    const original = window.fetch.bind(window);
    const resilientFetch = createResilientFetch({ ...options, fetch: original });
    window.addEventListener('pagehide', () => {
      pageHideCount += 1;
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && fallbackIssued) window.location.reload();
    });
    window.fetch = resilientFetch;
    installed = true;
  } catch {
    // A failed setup must leave window.fetch untouched.
  }
}
